// TypeScript types for First Processing module

import type { DecimalPrecision, ChartDataPoint } from '@/types/common';

// Re-export ChartDataPoint for backward compatibility
export type { ChartDataPoint };

export interface ProcessingParameters {
  tss: string;
  offset: string;
  mode: ProcessingMode;
  intrplMax: string;
  decimalPrecision?: DecimalPrecision;
}

// Re-export FirstProcessingParams from the uploader for consistency
export interface FirstProcessingParams {
  tss: number;
  offset: number;
  mode: string;
  intrplMax?: number;
  decimalPrecision?: DecimalPrecision;
  anchorTime?: string;
}

export type ProcessingMode = 'mean' | 'nearest' | 'nearest (mean)' | 'intrpl' | '';

export interface ProcessingModeOption {
  value: ProcessingMode;
  label: string;
  description?: string;
  requiresAdditionalInput?: boolean;
}

export interface FileMetadata {
  name: string;
  size: number;
  lastModified: number;
  content?: string;
  timeMinRaw?: string;
  timeMaxRaw?: string;
}

// ChartDataPoint is now imported from common.ts and re-exported above

export interface ProcessingState {
  workflow: {
    currentStep: number;
    completedSteps: number[];
  };
  file: {
    content: string | null;
    metadata: FileMetadata | null;
  };
  parameters: ProcessingParameters;
  processing: {
    status: 'idle' | 'uploading' | 'processing' | 'completed' | 'error';
    progress: StandardUploadProgress | null;
    error: string | null;
  };
  data: {
    table: string[][];
    chart: ChartDataPoint[];
  };
  ui: {
    loading: boolean;
    successMessage: string | null;
    errorMessage: string | null;
    isNameModalOpen: boolean;
    saveDataName: string;
  };
}

export interface ValidationErrors {
  tss?: string;
  offset?: string;
  mode?: string;
  intrplMax?: string;
  file?: string;
}

export interface ProcessingWorkflowStep {
  id: number;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  validation?: () => ValidationErrors | null;
}

// Re-export from ProgressTracker for consistency
// Dva odvojena progress bara:
// - uploadProgress: Frontend kontrolira (chunk upload) 0-100%
// - processingProgress: Backend kontrolira (CSV obrada) 0-100%
export interface StandardUploadProgress {
  fileName: string;
  uploadProgress: number;      // 0-100% - Frontend chunk upload
  processingProgress: number;  // 0-100% - Backend processing
  status: 'uploading' | 'processing' | 'completed' | 'error';
  error?: string;
  message?: string;
  step?: string;               // Backend step: chunk_assembly, parsing, preprocessing, processing, streaming, complete
  totalSize?: number;
  uploadedSize?: number;
  speed?: number;
  uploadId?: string;
  eta?: number;                // Estimated time remaining in seconds
  etaFormatted?: string;       // Formatted ETA string (e.g., "2m 30s")
  currentStep?: number;        // Current processing step (from backend)
  totalSteps?: number;         // Total processing steps (from backend)
}

export interface ProcessingValidationRules {
  tss: {
    min: number;
    max: number;
    required: boolean;
    pattern?: RegExp;
  };
  offset: {
    min: number;
    max: number;
    required: boolean;
    pattern?: RegExp;
  };
  intrplMax: {
    min: number;
    max: number;
    required: boolean;
    conditionalOn: ProcessingMode;
    pattern?: RegExp;
  };
}

export const VALIDATION_RULES: ProcessingValidationRules = {
  tss: {
    min: 0.0001,
    max: 1440,
    required: true,
    pattern: /^\d+(\.\d+)?$/
  },
  offset: {
    min: 0,
    max: 1440,
    required: true,
    pattern: /^\d+(\.\d+)?$/
  },
  intrplMax: {
    min: 0.0001,
    max: 1440,
    required: true,
    conditionalOn: 'intrpl',
    pattern: /^\d+(\.\d+)?$/
  }
};

export const PROCESSING_MODE_OPTIONS: ProcessingModeOption[] = [
  { 
    value: 'mean', 
    label: 'Mean', 
    description: 'Berechnet Durchschnittswerte für jedes Zeitintervall' 
  },
  { 
    value: 'nearest', 
    label: 'Nearest', 
    description: 'Verwendet den nächstgelegenen Wert innerhalb der Toleranz' 
  },
  { 
    value: 'nearest (mean)', 
    label: 'Nearest (Mean)', 
    description: 'Kombination aus Nearest und Mean Methoden' 
  },
  { 
    value: 'intrpl', 
    label: 'Interpolation', 
    description: 'Interpoliert Werte zwischen Datenpunkten',
    requiresAdditionalInput: true 
  }
];

export const WORKFLOW_STEPS: ProcessingWorkflowStep[] = [
  { id: 1, label: 'workflow.steps.upload', description: 'workflow.steps.uploadDesc' },
  { id: 2, label: 'workflow.steps.timeGrid', description: 'workflow.steps.timeGridDesc' },
  { id: 3, label: 'workflow.steps.imputation', description: 'workflow.steps.imputationDesc' },
  { id: 4, label: 'workflow.steps.process', description: 'workflow.steps.processDesc' },
  { id: 5, label: 'workflow.steps.save', description: 'workflow.steps.saveDesc' }
];

// =============================================================================
// UI STATE MANAGEMENT
// =============================================================================

/** UI State for the First Processing component */
export interface FirstProcessingUIState {
  currentStep: number;
  completedSteps: number[];
  isNameModalOpen: boolean;
  saveDataName: string;
  errorMessages: string[];
  successMessage: string | null;
  isSaving: boolean;
  isProcessing: boolean;
  showFileUpload: boolean;
  showActionButtons: boolean;
  errorCode: string | null;
  errorSuggestion: string | null;
  showUpgradeModal: boolean;
  upgradeMessage: string;
  upgradeResourceType: 'upload' | 'processing' | 'storage';
  decimalPrecision: DecimalPrecision;
}

/** Action types for UI reducer */
export type FirstProcessingUIAction =
  | { type: 'SET_STEP'; payload: number }
  | { type: 'MARK_STEP_COMPLETED'; payload: number }
  | { type: 'TOGGLE_MODAL'; payload: boolean }
  | { type: 'SET_SAVE_NAME'; payload: string }
  | { type: 'ADD_ERROR'; payload: string }
  | { type: 'CLEAR_ERRORS' }
  | { type: 'SET_SUCCESS'; payload: string | null }
  | { type: 'SET_SAVING'; payload: boolean }
  | { type: 'SET_PROCESSING'; payload: boolean }
  | { type: 'RESET_UI' }
  | { type: 'RESET_UI_WITH_SUCCESS'; payload: string }
  | { type: 'SET_ERROR_PAYLOAD'; payload: { message: string; code?: string; suggestion?: string } }
  | { type: 'SET_UPGRADE_MODAL'; payload: { open: boolean; message?: string; resourceType?: 'upload' | 'processing' | 'storage' } }
  | { type: 'SET_DECIMAL_PRECISION'; payload: DecimalPrecision };